import React, { useState, useEffect } from "react";
import { Box, Typography, Paper, useTheme, useMediaQuery, Card, CardContent, Divider, CircularProgress, TextField, IconButton, Tooltip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Button, Snackbar, Alert, LinearProgress } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import NoDataComponent from "../../components/NoDataComponent";
import CustomNoDataOverlay from "../../components/CustomNoDataOverlay";
import { useAuth } from "../../context/AuthContext";
import { VscNewFile } from "react-icons/vsc";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import SkeletonLoader from "../../components/SkeletonLoader";
import LoadingOverlay from "../../components/LoadingOverlay";
import { motion } from "framer-motion";

// FORMAT INR
const formatINR = (val) =>
    Number(val || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

export default function PayableDetails({ connectionString }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isSmall = useMediaQuery(theme.breakpoints.down(500));
    const navigate = useNavigate();

    const { selectedCompany } = useAuth();

    const [rows, setRows] = useState([]);
    // PDF column: spinner id + click handler (PDF generation to be wired later).
    const [loadingPdfId, setLoadingPdfId] = useState(null);
    const handlePdfClick = async (event, row) => {
        event.stopPropagation();
        if (loadingPdfId) return;
        setLoadingPdfId(row.id);
        // Tab opened first (synchronously) so the browser doesn't block the popup; it is then pointed at the
        // format saved in Printing Utilities for this row's BookName (BKP / CBP / BKR / CBR) — Format 1 if none is saved.
        const pdfWindow = window.open('', '_blank');
        let formatNo = 1;
        try {
            const fmtResp = await axios.get(`${API_URL}/companies`, { params: { companyId, t: new Date().getTime() }, timeout: 15000 });
            let companies = [];
            if (fmtResp?.data) {
                if (Array.isArray(fmtResp.data.companies)) companies = fmtResp.data.companies;
                else if (Array.isArray(fmtResp.data)) companies = fmtResp.data;
                else if (Array.isArray(fmtResp.data.data)) companies = fmtResp.data.data;
            }
            const rec = companies.find(c => Number(c?.CompId ?? c?.CompID ?? 0) === Number(companyId)) || companies[0];
            const norm = (s) => String(s || '').replace(/\s+/g, '').toLowerCase();
            const saved = (rec?.formats || []).find(f => norm(f?.BookName) === norm(row.bookName) && Number(f?.BookID ?? f?.BookId ?? 0));
            if (saved) formatNo = Number(saved.BookID ?? saved.BookId);
        } catch (err) {
            console.warn("Could not read the saved PDF format, using Format 1:", err?.message);
        }
        const pdfUrl = `/payreceive/${formatNo === 2 ? 'format_2/' : ''}${row.id}?companyId=${encodeURIComponent(companyId)}&type=payment`;
        if (pdfWindow) {
            pdfWindow.opener = null;
            pdfWindow.location.href = pdfUrl;
        } else {
            window.open(pdfUrl, '_blank', 'noopener,noreferrer');
        }
        try {
            const res = await axios.get(`${API_URL}/get-payReceive-pdf-data`, {
                params: { receiptId: row.id, companyId, type: "payment" }
            });
            console.log("Payment PDF data:", JSON.stringify(res.data, null, 2));
        } catch (err) {
            console.error("Payment PDF data fetch failed:", err?.response?.data || err);
        } finally {
            setLoadingPdfId(null);
        }
    };
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const companyId = selectedCompany?.CompId;
    const API_URL = `${connectionString}/api`;

    console.log("api url", API_URL)
    const [selectionModel, setSelectionModel] = useState([]);

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    // =========================
    // FETCH Payment LIST
    // =========================
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const res = await axios.get(`${API_URL}/get-payReceive-list`, {
                    params: {
                        companyId,
                        type: "payment"
                    }
                });

                const data = res.data?.data || [];

                const mapped = data.map((row) => {

                    // =========================
                    // CALCULATE PAYMENT TYPE
                    // =========================

                    let paymentType = "";

                    if (Number(row.IsAdvancePayment) === 1) {

                        paymentType = "Advance";

                    } else if (Number(row.billwise) === 1) {

                        paymentType = "Bill Wise";

                    } else if (Number(row.OnAccount) === 1) {

                        paymentType = "On Account";

                    } else {

                        paymentType = "-";
                    }

                    return {

                        id: row.id,

                        receiptDate:
                            row.ReceiptDate
                                ? new Date(row.ReceiptDate)
                                    .toLocaleDateString("en-GB")
                                : "",

                        receiptNo:
                            row.ReceiptNumber,

                        bookName:
                            row.BookName,

                        paymentType,

                        bankName:
                            row.BankName,

                        partyName:
                            row.PartyName,

                        chequeNo:
                            row.ChequeNo,

                        currency:
                            row.CurrencyName,

                        amount:
                            row.Amount,

                        bankCharges:
                            row.BankComm,

                        tds:
                            row.TDSAmt,

                        roundOff:
                            row.RoundOffAmt,

                        totalAmount:
                            row.PaidAmt,

                        remark:
                            row.Remark,

                        // optional raw flags
                        billwise:
                            row.billwise,

                        isAdvancePayment:
                            row.IsAdvancePayment,

                        onAccount:
                            row.OnAccount
                    };
                });

                setRows(mapped);
            } catch (err) {
                console.error("Payment Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };

        if (companyId) fetchData();
    }, [companyId]);

    // =========================
    // SEARCH FILTER
    // =========================
    const filteredRows = rows.filter((row) =>
        [
            row.receiptNo,
            row.partyName,
            row.bankName,
            row.chequeNo,
            row.paymentType,
            row.receiptDate,
            row.currency,
            row.remark,
        ]
            .join(" ")
            .toLowerCase()
            .includes(searchText.toLowerCase())
    );


    const handleDelete = (row) => {
        const ids =
            selectionModel.length > 0
                ? selectionModel
                : row
                    ? [row.id]
                    : [];

        if (!ids.length) return;

        setPendingDeleteIds(ids);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (deleting) return;
        setDeleting(true);

        try {

            const res = await axios.post(
                `${API_URL}/delete-payReceive`,
                {
                    receiptIds: pendingDeleteIds,
                    CompanyId: companyId,
                }
            );

            const {
                deletedIds = [],
                referencedIds = []
            } = res.data;

            // remove only deleted rows from UI
            setRows((prev) =>
                prev.filter((r) => !deletedIds.includes(r.id))
            );

            // remove deleted ids from selection
            setSelectionModel((prev) =>
                prev.filter((id) => !deletedIds.includes(id))
            );

            // =========================
            // snackbar message
            // =========================

            let message = "";

            if (deletedIds.length && referencedIds.length) {

                message =
                    `Deleted Successfully`;

            } else if (deletedIds.length) {

                message =
                    `Deleted Successfully`;

            } else if (referencedIds.length) {

                message =
                    `Cannot delete record(s) because advance reference exists`;

            }

            setSnackbar({
                open: true,
                message,
                severity:
                    deletedIds.length > 0
                        ? "success"
                        : "warning",
            });

        } catch (err) {

            console.error("Delete Payment Error:", err);

            setSnackbar({
                open: true,
                message:
                    err?.response?.data?.message ||
                    "Delete failed",
                severity: "warning",
            });
        } finally {
            setDeleting(false);
            setOpenDeleteDialog(false);
            setPendingDeleteIds([]);
        }
    };


    const handleSelectAllVisible = () => {
        const visibleIds = filteredRows.map(row => row.id);

        const allSelected = visibleIds.every(id =>
            selectionModel.includes(id)
        );

        if (allSelected) {
            setSelectionModel(prev =>
                prev.filter(id => !visibleIds.includes(id))
            );
        } else {
            setSelectionModel(prev => [
                ...new Set([...prev, ...visibleIds])
            ]);
        }
    };

    const allVisibleSelected =
        filteredRows.length > 0 &&
        filteredRows.every(row => selectionModel.includes(row.id));

    // =========================
    // DATAGRID COLUMNS
    // =========================
    const columns = [

        {
            field: "receiptDate",
            headerName: "Date",
            flex: 1
        },

        {
            field: "receiptNo",
            headerName: "Payment No",
            flex: 1.5,

            renderCell: ({ value, row }) => (

                <Typography
                    sx={{
                        color: "blue",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontSize: 15
                    }}

                    onClick={() =>
                        navigate(
                            `/main/payable-edit/${row.id}`
                        )
                    }
                >
                    {value}
                </Typography>
            ),
        },

        {
            field: "paymentType",
            headerName: "Payment Type",
            flex: 1.1
        },
        {
            field: "partyName",
            headerName: "Party Name",
            flex: 1.8,

            renderCell: ({ value }) => (
                <Typography
                    sx={{
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        lineHeight: "20px"
                    }}
                >
                    {value}
                </Typography>
            )
        },

        {
            field: "bankName",
            headerName: "Bank A/C",
            flex: 1.5
        },


        {
            field: "chequeNo",
            headerName: "Cheque No",
            flex: 1
        },

        // ✅ SINGLE AMOUNT COLUMN
        {
            field: "amountDetails",

            headerName: "Total Amount",

            flex: 1.8,

            sortable: true,

            valueGetter: (value, row) => (row.totalAmount === null || row.totalAmount === undefined ? "" : String(row.totalAmount)),

            renderCell: ({ row }) => (

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        // py: 1,
                        // gap: 0.4,
                        // lineHeight: 1.6
                    }}
                >

                    <Typography
                        sx={{
                            fontSize: "1rem",
                            fontWeight: "bold",
                            color: "#1976d2"
                        }}
                    >
                        Total: ₹{formatINR(row.totalAmount)}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem" }}>
                        Amt: {formatINR(row.amount)}
                    </Typography>

                    <Typography sx={{ fontSize: "0.78rem" }}>
                        TDS: {formatINR(row.tds)}
                    </Typography>

                    <Typography sx={{ fontSize: "0.78rem" }}>
                        RoundOff: {formatINR(row.roundOff)}
                    </Typography>

                    <Typography sx={{ fontSize: "0.78rem" }}>
                        Bank Chg: {formatINR(row.bankCharges)}
                    </Typography>


                </Box>
            ),
        },

        {
            field: "currency",
            headerName: "Currency",
            flex: 0.8
        },

        {
            field: "remark",
            headerName: "Remark",
            flex: 2
        },

        // PDF COLUMN
        {
            field: "pdf", headerName: "Action", flex: 0.4, sortable: false, filterable: false, align: "center", headerAlign: "center",
            renderCell: (params) => {
                const thisId = params.row.id;
                if (loadingPdfId && Number(loadingPdfId) === Number(thisId)) {
                    return <CircularProgress size={20} />;
                }
                return (
                    <Tooltip title="PDF">
                        <IconButton color="primary" onClick={(e) => { handlePdfClick(e, params.row); }}>
                            <PrintIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        },

        // DELETE COLUMN
        {
            field: "delete",

            headerName: "",

            flex: 0.5,

            sortable: false,

            filterable: false,

            align: "center",

            renderCell: (params) => {

                const isSelected =
                    selectionModel.includes(
                        params.row.id
                    );

                return (

                    <Tooltip
                        title={
                            isSelected
                                ? "Delete Selected"
                                : "Select row to enable"
                        }
                    >
                        <span>

                            <IconButton
                                color="error"

                                disabled={!isSelected}

                                onClick={(e) => {

                                    e.stopPropagation();

                                    handleDelete(
                                        params.row
                                    );
                                }}
                            >
                                <DeleteIcon />
                            </IconButton>

                        </span>
                    </Tooltip>
                );
            },
        },
    ];

    // =========================
    // MOBILE CARD
    // =========================
    // const MobileCard = ({ row }) => {
    //     const isSelected = selectionModel.includes(row.id);

    //     const toggleSelection = (e) => {
    //         e.stopPropagation();
    //         setSelectionModel(prev =>
    //             prev.includes(row.id)
    //                 ? prev.filter(id => id !== row.id)
    //                 : [...prev, row.id]
    //         );
    //     };

    //     return (
    //         <Card
    //             onClick={() => navigate(`/main/payable-edit/${row.id}`)}
    //             sx={{
    //                 mb: 1.8,
    //                 borderLeft: '5px solid #1976d2',
    //                 borderRadius: 2,
    //                 boxShadow: 2,
    //                 cursor: 'pointer',
    //                 '&:hover': { boxShadow: 4 }
    //             }}
    //         >
    //             <CardContent sx={{ py: 1.5 }}>

    //                 {/* 🔥 TOP ROW (EXACT LIKE PRODUCTION) */}
    //                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

    //                     {/* CHECKBOX */}
    //                     <Checkbox
    //                         checked={isSelected}
    //                         onChange={toggleSelection}
    //                         onClick={(e) => e.stopPropagation()}
    //                         size="small"
    //                     />

    //                     {/* Receipt INFO */}
    //                     <Box sx={{ flex: 1 }}>
    //                         <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>
    //                             {row.receiptNo}
    //                         </Typography>
    //                         <Typography sx={{ fontSize: "0.75rem", color: "#777" }}>
    //                             {row.billDate}
    //                         </Typography>
    //                     </Box>
    //                 </Box>

    //                 {/* DETAILS */}
    //                 <Box sx={{ mt: 1 }}>
    //                     {/* <Typography sx={{ fontSize: "0.8rem" }}>
    //                         <strong>Ref No:</strong> {row.refNo || "-"}
    //                     </Typography> */}

    //                     <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
    //                         <strong>Amount:</strong> ₹{formatINR(row.amount)}
    //                     </Typography>

    //                     <Typography sx={{ fontSize: "0.8rem", mt: 0.5 }}>
    //                         <strong>Remark:</strong> {row.remark || "-"}
    //                     </Typography>
    //                 </Box>
    //             </CardContent>
    //         </Card>
    //     );
    // };

    const MobileCard = ({ row }) => {
        const isSelected = selectionModel.includes(row.id);

        const toggleSelection = (e) => {
            e.stopPropagation();
            setSelectionModel(prev =>
                prev.includes(row.id)
                    ? prev.filter(id => id !== row.id)
                    : [...prev, row.id]
            );
        };

        return (
            <Card
                onClick={() => navigate(`/main/payable-edit/${row.id}`)}
                sx={{
                    mb: 1.8,
                    borderLeft: '5px solid #1976d2',
                    borderRadius: 2,
                    boxShadow: 2,
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 }
                }}
            >
                <CardContent sx={{ py: 1.5 }}>

                    {/* ROW 1: Checkbox + Payment No + Date */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Checkbox
                            checked={isSelected}
                            onChange={toggleSelection}
                            onClick={(e) => e.stopPropagation()}
                            size="small"
                        />
                        <Box sx={{ flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: '#1976d2' }}>
                                    {row.receiptNo}
                                </Typography>
                                <Typography sx={{ fontSize: "0.78rem", color: "#777" }}>
                                    {row.receiptDate}
                                </Typography>
                            </Box>

                            {/* Payment Type Badge */}
                            <Box sx={{ mt: 0.3 }}>
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: "0.7rem",
                                        fontWeight: 600,
                                        px: 1,
                                        py: 0.2,
                                        borderRadius: 1,
                                        bgcolor:
                                            row.paymentType === "Advance" ? "#fff3e0" :
                                                row.paymentType === "Bill Wise" ? "#e3f2fd" :
                                                    row.paymentType === "On Account" ? "#e8f5e9" : "#f5f5f5",
                                        color:
                                            row.paymentType === "Advance" ? "#e65100" :
                                                row.paymentType === "Bill Wise" ? "#1565c0" :
                                                    row.paymentType === "On Account" ? "#2e7d32" : "#757575",
                                    }}
                                >
                                    {row.paymentType}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Delete Button */}
                        <IconButton
                            color="error"
                            size="small"
                            disabled={!isSelected}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row);
                            }}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {/* ROW 2: Party + Bank */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", color: "#999", fontWeight: 600, textTransform: 'uppercase' }}>
                                Party
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 500 }}>
                                {row.partyName || "-"}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", color: "#999", fontWeight: 600, textTransform: 'uppercase' }}>
                                Bank A/C
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem" }}>
                                {row.bankName || "-"}
                            </Typography>
                        </Box>
                    </Box>

                    {/* ROW 3: Cheque No + Currency */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 0.8 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", color: "#999", fontWeight: 600, textTransform: 'uppercase' }}>
                                Cheque No
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem" }}>
                                {row.chequeNo || "-"}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontSize: "0.7rem", color: "#999", fontWeight: 600, textTransform: 'uppercase' }}>
                                Currency
                            </Typography>
                            <Typography sx={{ fontSize: "0.82rem" }}>
                                {row.currency || "-"}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 1 }} />

                    {/* ROW 4: Amount Breakdown */}
                    <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1.5, px: 1.5, py: 1 }}>

                        {/* Total — prominent */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: '#1976d2' }}>
                                Total Amount
                            </Typography>
                            <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: '#1976d2' }}>
                                ₹{formatINR(row.totalAmount)}
                            </Typography>
                        </Box>

                        {/* Sub amounts grid */}
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.4 }}>
                            {[
                                { label: "Amount", value: row.amount },
                                { label: "TDS", value: row.tds },
                                { label: "Round Off", value: row.roundOff },
                                { label: "Bank Chg", value: row.bankCharges },
                            ].map(({ label, value }) => (
                                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: "0.72rem", color: "#888" }}>{label}:</Typography>
                                    <Typography sx={{ fontSize: "0.72rem", color: "#555" }}>₹{formatINR(value)}</Typography>
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {/* ROW 5: Remark */}
                    {row.remark && (
                        <Typography sx={{ fontSize: "0.78rem", color: "#666", mt: 1, fontStyle: 'italic' }}>
                            📝 {row.remark}
                        </Typography>
                    )}

                </CardContent>
            </Card>
        );
    };

    // =========================
    // UI
    // =========================
    return (
        <Box sx={{ pt: 1, px: { xs: 2, sm: 3, md: 4 } }}>

            {/* HEADER */}

            <Box sx={{ position: "relative", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", mb: 1.5 }}>
                <Box sx={{ position: "absolute", left: 0 }}>
                    <Tooltip title="Add New Payment">
                        <IconButton component={Link} to="/main/payable-v2" sx={{ color: "orange" }}>
                            <VscNewFile size={25} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Typography
                    variant="h5"
                    sx={{
                        fontSize: { xs: "1.25rem", sm: "1.5rem" },
                        fontWeight: 600,
                        textAlign: "center",
                    }}>
                    {isSmall ? "Payment" : "Payment Detail"}
                </Typography>
            </Box>

            <Paper elevation={3} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 0.5 }}>

                    {/* MOBILE */}
                    {isMobile ? (
                        <>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search..."
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                sx={{ mb: 1.5 }}
                            />

                            {selectionModel.length > 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 2,
                                        px: 2,
                                        py: 1.2,
                                        borderRadius: 2,
                                        bgcolor: '#fff',
                                        boxShadow: 1
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={allVisibleSelected}
                                            onChange={handleSelectAllVisible}
                                            size="small"
                                        />
                                        <Typography sx={{ fontSize: "0.85rem", ml: 1 }}>
                                            {selectionModel.length} selected
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        color="error"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDelete();
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            )}

                            {loading && <SkeletonLoader count={3} height={120} />}
                            {!loading && rows.length === 0 && <NoDataComponent />}
                            {!loading && filteredRows.length === 0 && <CustomNoDataOverlay />}
                            {!loading && filteredRows.length > 0 && filteredRows.map((row) => (
                                <MobileCard key={row.id} row={row} />
                            ))}
                        </>
                    ) : (
                        <Box sx={{ height: "78vh", width: "100%", position: 'relative' }}>
                            <LoadingOverlay isVisible={loading} />
                            {!loading && rows.length === 0 && <NoDataComponent />}
                            {!loading && rows.length > 0 && <DataGrid
                                    rows={filteredRows}
                                    getRowHeight={() => 'auto'}
                                    columns={columns}
                                    getRowId={(row) => row.id}
                                    checkboxSelection
                                    rowSelectionModel={selectionModel}
                                    onRowSelectionModelChange={(newSel) =>
                                        setSelectionModel(newSel.map(Number))
                                    }
                                    pageSizeOptions={[50, 100]}
                                    initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
                                    sx={{
                                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#E0E0E0' },
                                        '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 700 },
                                        '& .MuiDataGrid-columnHeader': { backgroundColor: '#E0E0E0', color: '#000000', fontWeight: 'bold' },
                                        '& .MuiDataGrid-filler': { backgroundColor: '#FFFFFF' },
                                        '& .MuiDataGrid-scrollbarFiller': { backgroundColor: '#E0E0E0' },
                                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#FFFFFF' },
                                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#F9FAFB' },
                                        '& .MuiDataGrid-row:hover': { backgroundColor: '#f1f5f9' },
                                        '& .MuiDataGrid-row.Mui-selected': { backgroundColor: '#BDBDBD !important' },
                                        '& .MuiDataGrid-row': {
                                            minHeight: '100px !important',
                                            maxHeight: 'none !important',
                                        },
                                        '& .MuiDataGrid-cell': {
                                            display: 'flex',
                                            alignItems: 'center',        // centered
                                            whiteSpace: 'normal !important',
                                            wordBreak: 'break-word',
                                            lineHeight: '20px',
                                            overflow: "visible !important"
                                        },
                                        '& .MuiDataGrid-cellContent': {
                                            whiteSpace: 'normal !important',
                                            overflowWrap: 'break-word',
                                            lineHeight: '20px',
                                            textOverflow: 'unset'
                                        },
                                    }}
                                    slots={{
                                        noResultsOverlay: CustomNoDataOverlay,
                                    }}
                                />}
                        </Box>
                    )}
                </Box>

                <Dialog
                    open={openDeleteDialog}
                    onClose={() => { if (!deleting) setOpenDeleteDialog(false); }}
                    maxWidth="xs"
                    fullWidth
                >
                    {deleting
                        ? <LinearProgress color="error" sx={{ height: 4 }} />
                        : <Box sx={{ height: 4, background: 'linear-gradient(90deg, #d32f2f 0%, #f44336 100%)' }} />}
                    <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: 'error.main', fontSize: '1.05rem' }}>Confirm Deletion</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2.5, py: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Are you sure you want to delete{' '}
                            <Typography component="span" variant="body2" fontWeight={700} color="error.main">{pendingDeleteIds.length}</Typography>
                            {' '}record(s)? This action cannot be undone.
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="inherit" size="small" disabled={deleting}>Cancel</Button>
                        <Button color="error" variant="contained" size="small" disabled={deleting}
                            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <DeleteIcon />}
                            onClick={confirmDelete}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                    </Box>
                </Dialog>
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert
                    severity={snackbar.severity}
                    variant="filled"
                    onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}